import { useCallback, useContext, useEffect, useRef } from "react";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { message } from "utils/ciq-message";
import {
  ErrorMessages,
  ProjectPermissionEnum,
  SuccessMessages
} from "constants/index";
import {
  // useCustomReports,
  useCustomReportsDispatch
} from "../context/custom-reports-provider";
import { QUERY_CUSTOM_REPORT_DETAILS_BY_ID } from "../gql/query";
import { MUTATION_UPDATE_PROJECT_REPORT } from "../gql/mutation";
import { ContextColumnConfigType } from "../models";
import { ensureReportColumnsInConfig } from "../utils/utils";

const DATE_BLOCK_HEADER_PATTERNS: Array<[RegExp, (index: string) => string]> = [
  // New date-block keys (tool panel / grid colIds: milestones__, offsets__)
  [/^milestones__milestone_(\d+)_name$/, (i) => `Milestone Name ${i}`],
  [/^milestones__name_milestone_(\d+)$/, (i) => `Milestone Name ${i}`],
  [/^milestones__planned_milestone_(\d+)$/, (i) => `Planned Date ${i}`],
  [/^milestones__projected_milestone_(\d+)$/, (i) => `Projected Date ${i}`],
  [/^milestones__actual_milestone_(\d+)$/, (i) => `Actual Date ${i}`],
  [/^offsets__offset_(\d+)_name$/, (i) => `Duration Name ${i}`],
  [/^offsets__name_offset_(\d+)$/, (i) => `Duration Name ${i}`],
  [/^offsets__offset_(\d+)_duration$/, (i) => `Duration ${i}`],
  // Legacy unprefixed date-block keys (saved reports)
  [/^name_milestone_(\d+)$/, (i) => `Milestone Name ${i}`],
  [/^planned_milestone_(\d+)$/, (i) => `Planned Date ${i}`],
  [/^projected_milestone_(\d+)$/, (i) => `Projected Date ${i}`],
  [/^actual_milestone_(\d+)$/, (i) => `Actual Date ${i}`],
  [/^name_offset_(\d+)$/, (i) => `Duration Name ${i}`],
  [/^offset_(\d+)_duration$/, (i) => `Duration ${i}`]
];

const getDefaultHeaderForFieldKey = (fieldKey: string): string | undefined => {
  if (fieldKey === "milestones") return "Milestones";
  if (fieldKey === "offsets") return "Durations";

  for (const [pattern, toHeader] of DATE_BLOCK_HEADER_PATTERNS) {
    const match = pattern.exec(fieldKey);
    if (match) return toHeader(match[1]);
  }

  return undefined;
};

const normalizeColumnCaptionsForUpdate = (
  columnConfig: ContextColumnConfigType[]
): ContextColumnConfigType[] => {
  return columnConfig.map((col) => {
    const next: ContextColumnConfigType = { ...col };

    const defaultHeader = getDefaultHeaderForFieldKey(next.field_key);

    // If caption is missing or incorrectly stored as key, replace with header text.
    const captionLooksLikeKey =
      !next.caption_en || next.caption_en === next.field_key;

    // For non-renamable columns, we always want the real header text.
    if (next.allow_rename === false) {
      if (defaultHeader) next.caption_en = defaultHeader;
      else if (captionLooksLikeKey && next.original_caption_en) {
        next.caption_en = next.original_caption_en;
      }
    } else if (captionLooksLikeKey) {
      // For renamable columns, only fix clearly-bad captions (key/empty).
      if (defaultHeader) next.caption_en = defaultHeader;
      else if (next.original_caption_en) {
        next.caption_en = next.original_caption_en;
      }
    }

    if (Array.isArray(next.fields)) {
      next.fields = normalizeColumnCaptionsForUpdate(next.fields);
    }

    return next;
  });
};

function useManageConfigs(customReportId: string) {
  const dispatch: any = useCustomReportsDispatch();

  const { gqlClientForProject, tokenContents, columnHeaders } =
    useContext(ProjectContext);
  const materialCustomColumns = columnHeaders?.materialCustomColumns;
  const materialCustomColumnsRef = useRef(materialCustomColumns);
  materialCustomColumnsRef.current = materialCustomColumns;
  // column-headers builds a new array on every load; key on content instead.
  const materialCustomColumnsKey = JSON.stringify(materialCustomColumns ?? []);

  const canConfigureCustomReports = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanConfigureCustomReports,
    tokenContents?.role ?? ""
  );

  const { data: customReportDetails } = useCIQQuery(
    QUERY_CUSTOM_REPORT_DETAILS_BY_ID,
    {
      variables: { id: customReportId },
      client: gqlClientForProject,
      skip: !gqlClientForProject
    }
  );

  const [updateCustomReport] = useCIQMutation(MUTATION_UPDATE_PROJECT_REPORT, {
    client: gqlClientForProject
  });

  useEffect(() => {
    if (!customReportDetails?.project_reports_by_pk?.query_configs) return;

    const queryConfigs =
      customReportDetails.project_reports_by_pk.query_configs;

    const groupByFieldKey =
      queryConfigs.group_by_config?.field_keys?.length > 0
        ? queryConfigs.group_by_config?.field_keys[0]
        : "none";

    const datasetConfig = {
      groupByField: groupByFieldKey,
      additional_config: queryConfigs.additional_config,
      linkedEntity: queryConfigs.linked_entity_config
    };

    dispatch({
      type: "UPDATE_ALL_CONFIGS",
      payload: {
        detail: {
          id: customReportDetails.project_reports_by_pk.id,
          name: queryConfigs.name,
          description: queryConfigs.description
        },
        datasetConfig,
        columnConfig: ensureReportColumnsInConfig(
          queryConfigs.columns_config,
          materialCustomColumnsRef.current ?? []
        ),
        conditionalsFormatting: queryConfigs.conditionals_formatting ?? [],
        gridState: {
          sortState: queryConfigs.sorts_config,
          filterModel: queryConfigs.filters_config
        },
        permissions: {
          canConfigureCustomReports
        }
      }
    });
  }, [dispatch, customReportDetails, canConfigureCustomReports]);

  // Custom columns that load after the report merge into the current column
  // config instead of reloading the report, so later edits are kept.
  useEffect(() => {
    if (!materialCustomColumnsRef.current?.length) return;
    dispatch({
      type: "ENSURE_REPORT_COLUMNS",
      payload: materialCustomColumnsRef.current
    });
  }, [dispatch, materialCustomColumnsKey]);

  const onUpdateProjectReport = useCallback(
    async (data: any, showSuccessMessage = false) => {
      if (!canConfigureCustomReports) return;

      const normalizedData = { ...data };
      if (data?.columns_config && Array.isArray(data.columns_config)) {
        normalizedData.columns_config = normalizeColumnCaptionsForUpdate(
          data.columns_config
        );
      }
      if (data?.conditionals_formatting !== undefined) {
        normalizedData.conditionals_formatting =
          data.conditionals_formatting ?? [];
      }

      const res = await updateCustomReport({
        variables: {
          project_report_id: customReportId,
          data: normalizedData
        }
      });
      if (res.data) {
        if (showSuccessMessage) {
          message.success(SuccessMessages.SuccessfullyUpdate);
        }
      } else {
        message.error(ErrorMessages.SomethingWentWrong);
      }
    },
    [canConfigureCustomReports, customReportId, updateCustomReport]
  );

  const onUpdateDatasetConfig = useCallback(
    async (datasetConfig: any) => {
      if (!canConfigureCustomReports) return;

      await onUpdateProjectReport({
        linked_entity_config: datasetConfig.linkedEntity,
        group_by_config: {
          type: "simple",
          field_keys: [datasetConfig.groupByField]
        },
        additional_config: datasetConfig.additional_config
      });
    },
    [canConfigureCustomReports, onUpdateProjectReport]
  );

  const onUpdateColumnConfig = useCallback(
    async (columnConfig: ContextColumnConfigType[]) => {
      if (!canConfigureCustomReports) return;

      const normalized = normalizeColumnCaptionsForUpdate(columnConfig);
      await onUpdateProjectReport({ columns_config: normalized });
    },
    [canConfigureCustomReports, onUpdateProjectReport]
  );

  const onUpdateConditionalsFormatting = useCallback(
    async (conditionalsFormatting: Array<any>) => {
      if (!canConfigureCustomReports) return;

      await onUpdateProjectReport({
        conditionals_formatting: conditionalsFormatting ?? []
      });
    },
    [canConfigureCustomReports, onUpdateProjectReport]
  );

  return {
    onUpdateDatasetConfig,
    onUpdateColumnConfig,
    onUpdateConditionalsFormatting,
    onUpdateProjectReport,
    canConfigureCustomReports
  };
}

export default useManageConfigs;
